import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { withRouter } from "react-router-dom";
import { get, map, size, filter, includes, concat } from "lodash";

import { fetchGet } from "../../api/fetch";

import { setBreadcrumb } from "../../actions/common";
import { expandItem, initiateExpandSection } from "../../actions/companies";

import Grid from "@material-ui/core/Grid";

import CompaniesTableRow from "./CompaniesTableRow";
import ScrollableTable from "../Common/Table/ScrollableTable";
import CreateContainer from "./CreateContainer";

import { levels } from "../../constants/companies";

import {
  getQueryStringValue,
  setQueryStringValue,
} from "../../utils/queryString";

import colors from "../../assets/style/colors";

const styles = {
  mainContent: {
    width: "calc(100vw - 98px)",
    height: "calc(100vh - 148px)",
    minHeight: 0,
    margin: "24px",
    padding: "24px",
  },
  container: {
    height: "40px",
    width: "100%",
    backgroundColor: colors.white,
    paddingLeft: "24px",
    boxShadow: `0px 0px 4px 0px ${colors.black23}`,
  },
  tabs: {
    height: "40px",
    float: "left",
    minHeight: "40px",
  },
};

const columns = [
  {
    name: "site_company",
    minWidth: 220,
  },
  {
    name: "profiles_type",
    minWidth: 150,
  },
  {
    name: "profiles_sites",
    minWidth: 200,
  },
  {
    name: "companies_zones",
    minWidth: 200,
  },
  {
    name: "companies_assignedProfiles",
    minWidth: 200,
  },
  {
    name: "companies_devices",
    minWidth: 200,
  },
];

const Companies = (props) => {
  const { classes, dispatch } = props;
  const [companies, setCompanies] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);

  const { expandedCompanies, expandedSites, expandedZones } = get(props, [
    "companies",
    "root",
  ]) || {
    expandedCompanies: getQueryStringValue("expandedCompanies"),
    expandedSites: getQueryStringValue("expandedSites"),
    expandedZones: getQueryStringValue("expandedZones"),
  };

  React.useEffect(() => {
    dispatch(
      setBreadcrumb([
        { name: "admin", display: false },
        { name: I18n.get("topbar_companies") },
      ])
    );
    fetchCompanies();
    let newCompanies, newSites, newZones;
    if (size(expandedCompanies) > 0) {
      setQueryStringValue("expandedCompanies", expandedCompanies);
    } else {
      newCompanies = getQueryStringValue("expandedCompanies");
    }
    if (size(expandedSites) > 0) {
      setQueryStringValue("expandedSites", expandedSites);
    } else {
      newSites = getQueryStringValue("expandedSites");
    }
    if (size(expandedZones) > 0) {
      setQueryStringValue("expandedZones", expandedZones);
    } else {
      newZones = getQueryStringValue("expandedZones");
    }

    if (!get(props, ["companies", "root"])) {
      dispatch(initiateExpandSection("root", newCompanies, newSites, newZones));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onExpand = (expandedLevel, newId) => {
    // Dispatch action
    dispatch(expandItem("root", expandedLevel, newId));

    // Update URL
    if (includes(get(props, ["companies", "root", expandedLevel]), newId)) {
      setQueryStringValue(
        expandedLevel,
        filter(
          get(props, ["companies", "root", expandedLevel]),
          (i) => i !== newId
        )
      );
    } else {
      setQueryStringValue(
        expandedLevel,
        concat(get(props, ["companies", "root", expandedLevel]), [newId])
      );
    }
  };

  const fetchCompanies = async () => {
    try {
      setIsFetching(true);
      let companies = await fetchGet("companies");

      // Fetch sites for expanded companies
      for (let i = 0; i < size(expandedCompanies); i++) {
        let sites = await fetchGet(
          `companies/${get(expandedCompanies, [i])}/sites`
        );
        companies = map(companies, (item) =>
          get(item, ["id"]) === get(expandedCompanies, [i])
            ? { ...item, sites }
            : item
        );
      }

      // Fetch zones for expanded sites
      for (let i = 0; i < size(expandedSites); i++) {
        const companyId = get(
          filter(
            companies,
            (c) =>
              size(
                filter(
                  get(c, ["sites"]),
                  (s) => s.id === get(expandedSites, [i])
                )
              ) > 0
          ),
          [0, "id"]
        );
        if (companyId) {
          let zones = await fetchGet(
            `companies/${companyId}/sites/${get(expandedSites, [i])}/zones`
          );
          companies = map(companies, (item) =>
            get(item, ["id"]) === companyId
              ? {
                  ...item,
                  sites: map(get(item, ["sites"]), (s) =>
                    get(s, ["id"]) === get(expandedSites, [i])
                      ? { ...s, zones }
                      : s
                  ),
                }
              : item
          );
        }
      }

      setCompanies(companies);
      setIsFetching(false);
    } catch (e) {
      setIsFetching(false);
      console.log(e);
    }
  };

  const fetchSites = async (companyId) => {
    try {
      setIsFetching(true);
      let res = await fetchGet(`companies/${companyId}/sites`);
      setCompanies(
        map(companies, (item) =>
          get(item, ["id"]) === companyId ? { ...item, sites: res } : item
        )
      );
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  const fetchZones = async (companyId, siteId) => {
    try {
      setIsFetching(true);
      let res = await fetchGet(`companies/${companyId}/sites/${siteId}/zones`);
      setCompanies(
        map(companies, (item) =>
          get(item, ["id"]) === companyId
            ? {
                ...item,
                sites: map(get(item, ["sites"]), (s) =>
                  get(s, ["id"]) === siteId ? { ...s, zones: res } : s
                ),
              }
            : item
        )
      );
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  const fetchDevices = async (companyId, siteId, zoneId) => {
    try {
      setIsFetching(true);
      let res = await fetchGet(
        `companies/${companyId}/sites/${siteId}/zones/${zoneId}/devices`
      );
      //let devices = getDevicesTree(res, companyId, siteId, zoneId);
      let devices = res;
      setCompanies(
        map(companies, (item) =>
          get(item, ["id"]) === companyId
            ? {
                ...item,
                sites: map(get(item, ["sites"]), (s) =>
                  get(s, ["id"]) === siteId
                    ? {
                        ...s,
                        zones: map(get(s, ["zones"]), (z) =>
                          get(z, ["id"]) === zoneId
                            ? {
                                ...z,
                                devices,
                              }
                            : z
                        ),
                      }
                    : s
                ),
              }
            : item
        )
      );
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className={`mainContent ${classes.mainContent}`}
    >
      <Grid item style={{ marginBottom: "24px" }}>
        <Grid container>
          <Grid item>
            <CreateContainer
              dialog={true}
              refresh={fetchCompanies}
              level={levels.company}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item style={{ overflow: "auto", maxWidth: "1300px" }}>
        <ScrollableTable
          columns={columns}
          hasMore={false}
          isFetching={isFetching}
          stickyHeader
        >
          {map(companies, (item, index) => (
            <CompaniesTableRow
              key={get(item, ["id"])}
              index={index}
              item={item}
              fetchSites={fetchSites}
              fetchZones={fetchZones}
              fetchDevices={fetchDevices}
              level={levels.company}
              levelId={0}
              language={props.language}
              isLast={index === size(companies) - 1}
              expandedCompanies={expandedCompanies}
              expandedSites={expandedSites}
              expandedZones={expandedZones}
              onExpand={onExpand}
            />
          ))}
        </ScrollableTable>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
    companies: get(state, ["companies"]),
  };
};

export default withRouter(
  connect(mapStateToProps)(withStyles(styles)(Companies))
);
